import { CSSProperties, useEffect, useMemo, useState } from 'react';

/**
 * /share/text and /share/email — bridge routes for the cold-email share buttons.
 *
 * Gmail iOS's link rewriter only whitelists http/https, so inline <a href="sms:...">
 * or <a href="mailto:..."> can get stripped before the OS sees them. The fix: link
 * the email button to a hosted https route that performs the sms:/mailto: navigation
 * via window.location.href once the page loads. iOS picks up the scheme from the
 * navigation and opens Messages/Mail pre-filled.
 *
 * Same pattern as /copy. Mounted outside WedgeAuthGate so cold taps from email
 * don't bounce off the auth wall.
 */

type Via = 'text' | 'email';
type Status = 'redirecting' | 'manual' | 'missing-invite';

const SMS_BODY = (inviteUrl: string) =>
  `Try PREP. Walk into any account sharp in 30 seconds. Use my code: ${inviteUrl}`;

const EMAIL_SUBJECT = 'You should try PREP';
const EMAIL_BODY = (invite: string, inviteUrl: string) =>
  `Try PREP. Walk into any account sharp in 30 seconds. Use my code ${invite} to get 2 extra briefs: ${inviteUrl}`;

export function ShareRedirect({ via }: { via: Via }) {
  const { invite, inviteUrl } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get('invite') || '').trim();
    return {
      invite: code,
      inviteUrl: code
        ? `https://wedge.repvantage.io/?invite=${encodeURIComponent(code)}`
        : 'https://wedge.repvantage.io/',
    };
  }, []);

  const shareUrl = useMemo(() => {
    if (!invite) return '';
    if (via === 'text') {
      return `sms:?body=${encodeURIComponent(SMS_BODY(inviteUrl))}`;
    }
    return `mailto:?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(EMAIL_BODY(invite, inviteUrl))}`;
  }, [via, invite, inviteUrl]);

  const [status, setStatus] = useState<Status>('redirecting');

  useEffect(() => {
    if (!invite) {
      setStatus('missing-invite');
      return;
    }

    let cancelled = false;

    // Fire the navigation. iOS / Android intercepts the scheme and opens the app.
    window.location.href = shareUrl;

    // If we're still on the page after ~1.6s, the scheme didn't fire (desktop
    // browser, scheme blocked, etc.). Show manual fallback so the rep isn't stuck.
    const t = window.setTimeout(() => {
      if (!cancelled) setStatus('manual');
    }, 1600);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [shareUrl, invite]);

  const channelLabel = via === 'text' ? 'iMessage' : 'your email app';
  const channelVerb = via === 'text' ? 'Text' : 'Email';

  const status_title =
    status === 'missing-invite'
      ? 'Missing your invite code.'
      : status === 'manual'
        ? `${channelLabel} didn’t open.`
        : `Opening ${channelLabel}…`;

  const status_sub =
    status === 'missing-invite'
      ? 'The share link is malformed. Head back to your PREP email and re-tap.'
      : status === 'manual'
        ? 'Copy the message below and send it yourself.'
        : 'Pre-filled with your code and a link your friend can tap.';

  const manualMessage =
    via === 'text' ? SMS_BODY(inviteUrl) : `${EMAIL_SUBJECT}\n\n${EMAIL_BODY(invite, inviteUrl)}`;

  // Styles (matches wedge dark theme, same aesthetic as CopyCode.tsx)
  const wrap: CSSProperties = {
    minHeight: '100vh',
    background: '#0A1A10',
    color: '#E2E8F0',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'SF Pro Text', Inter, 'Helvetica Neue', system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    boxSizing: 'border-box',
  };

  const card: CSSProperties = {
    width: '100%',
    maxWidth: 440,
    background: 'rgba(52,211,153,0.04)',
    border: '1px solid rgba(52,211,153,0.15)',
    borderRadius: 16,
    padding: '44px 32px 36px',
    textAlign: 'center',
    boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
  };

  const wordmark: CSSProperties = {
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: -0.4,
    marginBottom: 8,
    color: '#F0F0F0',
    lineHeight: 1,
  };

  const tagline: CSSProperties = {
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: 1.8,
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: 32,
  };

  const statusStyle: CSSProperties = {
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: -0.2,
    lineHeight: 1.3,
    margin: '0 0 8px',
    color: '#E2E8F0',
  };

  const subStyle: CSSProperties = {
    fontSize: 13,
    lineHeight: 1.55,
    color: 'rgba(255,255,255,0.55)',
    margin: '0 0 24px',
  };

  const manualBox: CSSProperties = {
    display: status === 'manual' ? 'block' : 'none',
    background: 'rgba(245,158,11,0.06)',
    border: '1px solid rgba(245,158,11,0.25)',
    borderRadius: 8,
    padding: '14px 16px',
    fontFamily: "'SF Mono', ui-monospace, 'Cascadia Mono', Menlo, monospace",
    fontSize: 12.5,
    lineHeight: 1.55,
    color: '#F59E0B',
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    userSelect: 'all',
    marginBottom: 20,
  };

  const btn: CSSProperties = {
    display: 'inline-block',
    padding: '13px 28px',
    background: '#00875A',
    color: '#fff',
    textDecoration: 'none',
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 14.5,
    letterSpacing: 0.1,
    boxShadow: '0 6px 18px rgba(0,135,90,0.25)',
  };

  const footer: CSSProperties = {
    marginTop: 22,
    paddingTop: 18,
    borderTop: '1px solid rgba(255,255,255,0.06)',
    fontSize: 10,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    fontFamily: "'SF Mono', ui-monospace, Menlo, monospace",
    color: 'rgba(255,255,255,0.30)',
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={wordmark}>
          Rep<span style={{ color: '#F59E0B' }}>Vantage</span>
        </div>
        <div style={tagline}>Sales Intelligence Platform</div>

        <p style={statusStyle}>{status_title}</p>
        <p style={subStyle}>{status_sub}</p>

        <div style={manualBox}>{manualMessage}</div>

        <a href={shareUrl || 'https://wedge.repvantage.io/'} style={btn}>
          Try {channelVerb} again →
        </a>

        <div style={footer}>© 2026 RepVantage LLC · Made for the field</div>
      </div>
    </div>
  );
}
